import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'

import {
  useMarkRead,
  useMembers,
  useMessages,
  useOlderMessages,
  useThreads,
  useTrip,
} from '../../hooks/queries'
import { ApiError, ohneNetz } from '../../api/client'
import { vergissOrt } from '../../lib/letzterChat'
import { merkeUebergabe } from '../../lib/highlightUebergabe'
import type { Member, Message } from '../../api/types'
import { dayLabel, sameDay } from '../../lib/format'
import { BackIcon, InfoIcon } from '../../components/Icons'
import { Netzstreifen } from '../../components/Netzstreifen'
import { Reiseumschalter } from '../map/Reiseumschalter'
import { useOutbox } from '../../hooks/useOutbox'
import { Bubble } from './Bubble'
import { Composer } from './Composer'
import { Wartend } from './Wartend'

/** Worauf sich die Nachricht bezieht, die gerade geschrieben wird. */
export interface Bezug {
  message: Message
  /** Gesetzt, wenn es um genau ein Foto des Beitrags geht. */
  mediaId?: number
}

/**
 * Ein Chat.
 *
 * Ohne `userId` ist das die Reise selbst: für einen Empfänger der Einzelchat
 * mit dem Reisenden, für den Absender sein Rundbrief. Mit `userId` der
 * Antwortverlauf eines Empfängers -- den kann nur der Absender öffnen, und
 * das Backend prüft das noch einmal selbst.
 */
export function ThreadPage() {
  const params = useParams()
  const navigate = useNavigate()
  const tripId = Number(params.tripId)
  const withUser = params.userId ? Number(params.userId) : undefined

  const trip = useTrip(tripId)
  const messages = useMessages(tripId, withUser)
  const older = useOlderMessages(tripId, withUser)
  const markRead = useMarkRead(tripId, withUser)
  // Der Name des Empfängers kommt aus der Teilnehmerliste, nicht aus den
  // Nachrichten: wer noch nie geantwortet hat, käme darin gar nicht vor.
  const members = useMembers(tripId, withUser !== undefined)
  // Nur aus dem Zwischenspeicher gelesen -- die Liste nebenan fragt ohnehin.
  const threads = useThreads()
  // Was in diesem Verlauf noch auf Netz wartet -- steht unten, vor der
  // Eingabezeile, als wäre es schon gesendet. Denn das ist es aus Sicht
  // dessen, der es geschrieben hat.
  const wartende = useOutbox(tripId, withUser)

  // Worauf sich das bezieht, was gerade geschrieben wird. Der Zustand sitzt
  // hier und nicht im Composer: gesetzt wird er an einer Sprechblase weiter
  // oben, und die beiden kennen einander nur über diese Seite.
  //
  // Er steht bei den anderen Hooks und nicht weiter unten bei seinem Gebrauch:
  // darunter stehen frühe Rückgaben, und ein Hook hinter einer davon liefe
  // nicht bei jedem Durchgang.
  const [bezug, setBezug] = useState<Bezug | null>(null)

  // Beim Wechsel des Chats fällt der Bezug weg -- eine Nachricht aus der
  // vorigen Reise wäre hier weder zu sehen noch zulässig.
  useEffect(() => setBezug(null), [tripId, withUser])

  /**
   * Zur bezogenen Nachricht springen.
   *
   * Steht sie schon im Verlauf, geht es sofort dorthin, und sie leuchtet kurz
   * auf -- ohne das weiß niemand, wo man gelandet ist.
   *
   * Steht sie nicht da, wird **einmal** nachgeladen. Sich seitenweise rückwärts
   * durch einen Sommer zu laden wäre kein Sprung mehr, sondern eine Reise; wer
   * so weit zurück will, blättert selbst.
   */
  function springeZu(messageId: number, nachgeladen = false) {
    const ziel = document.getElementById(`msg-${messageId}`)
    if (ziel !== null) {
      ziel.scrollIntoView({ block: 'center', behavior: 'smooth' })
      ziel.classList.add('bubble-row--gefunden')
      window.setTimeout(() => ziel.classList.remove('bubble-row--gefunden'), 1600)
      return
    }

    const cursor = messages.data?.next_before ?? null
    if (nachgeladen || cursor === null || older.isPending) return
    older.mutate(cursor, {
      // Erst nach dem Zeichnen suchen: vorher gibt es das Element noch nicht.
      onSuccess: () => window.setTimeout(() => springeZu(messageId, true), 60),
    })
  }

  const newest = messages.data?.messages[0]?.id
  useEffect(() => {
    // Wer den Chat offen hat, hat gelesen. Ausgelöst wird das an der neuesten
    // Nachricht, nicht bei jedem Rendern -- sonst schriebe jedes Scrollen.
    if (newest !== undefined) markRead.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId, withUser, newest])

  // Ein fehlgeschlagener Abruf darf nicht wie ein leerer Chat aussehen --
  // sonst wirkt eine verweigerte Berechtigung wie „hier steht eben nichts“.
  //
  // **Ein Netzfehler gehört aber nicht dazu.** Aus dem Betrieb: im Flugmodus
  // stand hier „Dieser Chat lässt sich nicht öffnen", und damit war auch die
  // Eingabezeile weg -- also ausgerechnet der Weg in den Ausgangskorb, den es
  // für genau diesen Fall gibt. Ohne Empfang bleibt der Chat offen: was
  // geladen ist, bleibt lesbar, und schreiben geht weiter.
  const failure = trip.error ?? messages.error

  // Eine Reise, die es nicht mehr gibt, ist keine Meldung wert -- nur der Weg
  // zurück.
  //
  // Aus dem Betrieb, auf dem iPhone: die Web-App nimmt beim Öffnen die zuletzt
  // besuchte Seite wieder auf, und die zeigte auf eine gelöschte Reise. Beim
  // Start stand dann „Dieser Chat lässt sich nicht öffnen" — für etwas, das
  // niemand angeklickt hatte. Wer die Reise verlassen hat oder aus ihr
  // entfernt wurde, bekommt vom Server denselben Bescheid; auch dann gehört er
  // heraus und nicht in eine Sackgasse. Wohin, entscheidet dann der
  // Startbereich -- mit gelöschter Erinnerung ist das der oberste Chat.
  //
  // Alles andere (ein Serverfehler etwa) bleibt stehen und wird gesagt: dazu
  // gibt es nichts Besseres zu tun, und stillschweigend zu verschwinden wäre
  // die schlechtere Auskunft.
  const verschwunden = failure instanceof ApiError && failure.status === 404
  useEffect(() => {
    if (verschwunden) vergissOrt()
  }, [verschwunden])
  if (verschwunden) return <Navigate to="/" replace />

  if (failure && !ohneNetz(failure)) {
    return (
      <div className="empty">
        <p className="empty__title">Dieser Chat lässt sich nicht öffnen</p>
        <p>{failure.message}</p>
        <Link className="button button--quiet" to="/">
          Zurück zur Liste
        </Link>
      </div>
    )
  }

  const partner = members.data?.find((m) => m.user.id === withUser)
  const partnerName = partner?.user.display_name

  // Warum hier nichts zu schreiben ist -- oder null, wenn es geht.
  //
  // Beurteilt wird erst, wenn die Teilnehmerliste **da** ist: solange sie
  // lädt, ist „nicht gefunden" kein Befund, sondern ein Zwischenstand, und
  // „nicht mehr in dieser Reise" blitzte bei jedem Öffnen kurz auf.
  const stumm = withUser === undefined || !members.isSuccess ? null : grundOhneZeile(partner)

  // Und solange es noch offen ist, steht unten gar nichts. Eine Eingabezeile,
  // die eine Zehntelsekunde später verschwindet, ist dieselbe Verwirrung in
  // klein -- lieber einen Augenblick nichts als kurz ein falsches Versprechen.
  const wartetAufAuskunft = withUser !== undefined && members.isPending

  // Ohne Netz weiß die Reise selbst nichts mehr zu sagen -- die Chatliste
  // daneben aber schon: sie trägt Titel und Namen und liegt im
  // Zwischenspeicher. Ein Kopf mit „…" wäre schlechter als einer aus zweiter
  // Hand.
  const ausListe = threads.data?.find(
    (t) => t.trip_id === tripId && (t.with_user?.id ?? undefined) === withUser,
  )

  const title = withUser
    ? (partnerName ?? ausListe?.with_user?.display_name ?? 'Antworten')
    : (trip.data?.title ?? ausListe?.trip_title ?? '…')

  // Ohne Angaben zur Reise wird nichts behauptet: „Reisebericht von" mit
  // leerem Namen dahinter sah aus wie ein Fehler und war nur fehlender Empfang.
  // Dass kein Netz da ist, sagt der Streifen über der Eingabezeile -- hier
  // stünde es ein zweites Mal.
  const zweck = trip.data === undefined
    ? ''
    : withUser
    ? `Antworten in „${trip.data.title}“ – nur für dich sichtbar`
    : trip.data?.role === 'sender'
      ? `Rundbrief an ${(trip.data.member_count ?? 1) - 1} Empfänger` +
        // Wer wartet, wartet auf eine Entscheidung -- und die trifft niemand,
        // der nichts davon weiß. Die Reiseseite ist einen Tipp entfernt.
        (trip.data.pending_requests > 0
          ? ` · ${trip.data.pending_requests} ${trip.data.pending_requests === 1 ? 'Anfrage' : 'Anfragen'}`
          : '')
      : `Reisebericht von ${trip.data?.owner.display_name ?? ''}`

  // Die Beschreibung stand bisher nur auf der Infoseite -- also dort, wo man
  // sie beim Lesen nie sieht. Sie gehört in den Kopf des Chats, denn sie sagt,
  // worum es in dieser Reise geht.
  //
  // Eigene Zeile statt angehängt: hinter „Rundbrief an 5 Empfänger" wäre sie
  // auf einem Telefon nach drei Wörtern zu Ende. So bekommt sie zwei Zeilen,
  // und die Angaben darüber -- vor allem eine wartende Anfrage -- bleiben
  // unangetastet. Der Kopf wächst nur dort, wo es überhaupt etwas zu sagen
  // gibt.
  const beschreibung = withUser ? '' : (trip.data?.description ?? '').trim()

  const list = messages.data?.messages ?? []
  const olderCursor = messages.data?.next_before ?? null

  return (
    <>
      <header className="bar">
        <Link className="icon-button" to="/" aria-label="Zurück zur Liste">
          <BackIcon />
        </Link>
        <div className="bar__grow">
          <h2 className="bar__title">{title}</h2>
          <p className="bar__subtitle">{zweck}</p>
          {/* `title`: bei mehr als zwei Zeilen lässt sie sich wenigstens im
              Vorbeigehen lesen -- ganz steht sie auf der Infoseite. */}
          {beschreibung !== '' && (
            <p className="bar__desc" title={beschreibung}>
              {beschreibung}
            </p>
          )}
        </div>
        <Link
          className="icon-button"
          to={`/t/${tripId}/info`}
          aria-label="Angaben zur Reise"
        >
          <InfoIcon />
        </Link>
      </header>

      {/* Nur im Reise-Chat, nicht im Antwortverlauf eines Empfängers: die
          Karte gehört zur Reise, und ein Umschalter „Chat · Karte" über einem
          Zweiergespräch verspräche einen Wechsel, den es dort nicht gibt. */}
      {withUser === undefined && (
        <Reiseumschalter
          tripId={tripId}
          aktiv="chat"
          darfBearbeiten={trip.data?.role === 'sender'}
        />
      )}

      {/* column-reverse: die neueste Nachricht steht unten, und der Browser
          hält den Blick von selbst dort, wenn eine dazukommt. */}
      <div className="timeline">
        {messages.isPending && <p className="empty">Wird geladen …</p>}

        {!messages.isPending && list.length === 0 && wartende.length === 0 && (
          <div className="empty">
            {/* „Noch nichts geschrieben" wäre hier eine Behauptung über den
                Verlauf -- ohne Netz weiß die App darüber gerade nichts. */}
            {messages.data === undefined && ohneNetz(messages.error) ? (
              <>
                <p className="empty__title">Kein Netz</p>
                <p>
                  Der Verlauf kommt, sobald du wieder Empfang hast. Schreiben
                  kannst du trotzdem — es geht später von selbst raus.
                </p>
              </>
            ) : (
              <>
                <p className="empty__title">Noch nichts geschrieben</p>
                <p>
                  {withUser
                    ? `${partnerName ?? 'Diese Person'} hat noch nicht geantwortet.`
                    : trip.data?.role === 'sender'
                      ? 'Schreib die erste Nachricht – alle Empfänger bekommen sie zu lesen.'
                      : 'Sobald der Reisende etwas schreibt, erscheint es hier.'}
                </p>
              </>
            )}
          </div>
        )}

        {/* Vor der Liste, weil die Zeitleiste umgekehrt läuft (column-reverse):
            was hier steht, erscheint unten -- und dort gehört das hin, was als
            Nächstes rausgeht. */}
        {[...wartende].reverse().map((eintrag) => (
          <Wartend key={`wartet-${eintrag.key}`} eintrag={eintrag} />
        ))}

        {list.map((message, index) => {
          const next = list[index + 1]
          const startsDay = !next || !sameDay(message.created_at, next.created_at)
          return (
            <div key={message.id} style={{ display: 'contents' }}>
              <Bubble
                message={message}
                tripId={tripId}
                withUser={withUser}
                // Für die Kärtchen einer Freigabe: sie zeigen ein Datum, und
                // ein Datum ohne Uhr ist die halbe Angabe.
                reiseVersatz={trip.data?.tz_offset_min ?? null}
                showAuthor={!message.own && Boolean(withUser)}
                canReact={trip.data?.archived_at == null}
                // Wer das liest, entscheidet über den Wortlaut, und nur diese
                // Seite kennt beide Blickwinkel: der Mitleser soll nicht
                // glauben, eine persönliche Nachricht sei ein Rundbrief -- und
                // der Reisende sieht in ihrem Verlauf sonst nicht, was an alle
                // ging und was nur an sie.
                // Der Rückfall nennt keine Person, sondern „diese Person":
                // solange der Name noch nicht da ist, wäre jedes Fürwort
                // geraten -- und geraten heißt hier: bei jedem zweiten falsch.
                directLabel={withUser ? `Nur an ${partnerName ?? 'diese Person'}` : 'Nur an dich'}
                onReply={(ziel, mediaId) => setBezug({ message: ziel, mediaId })}
                onJump={springeZu}
                // Nur der Reisende, und nur im Reise-Chat: was in einem
                // Antwortverlauf steht, ist die Antwort eines Einzelnen und
                // gehört nicht auf eine Karte, die alle sehen.
                onAufDieKarte={
                  trip.data?.role === 'sender' && withUser === undefined
                    ? (ziel) => {
                        merkeUebergabe({
                          tripId,
                          // **Der Beitrag ist der Text, nicht der Titel.**
                          //
                          // Hier stand die erste Zeile als Titel und der Rest
                          // als Text -- „ein Reisebericht fängt fast immer mit
                          // einer an". Tut er nicht: die meisten Beiträge sind
                          // ein einziger Absatz, und der landete dann
                          // vollständig in der Titelzeile, während der Text
                          // leer blieb. Aus dem Betrieb gemeldet.
                          //
                          // Der Titel bleibt leer und füllt sich mit dem
                          // Ortsnamen, sobald er da ist -- derselbe Weg wie
                          // bei „Aus Foto/Video".
                          title: '',
                          body: ziel.body,
                          at: ziel.created_at,
                        })
                        // In die Werkbank, nicht in die Ansicht: ein Entwurf
                        // ist Werkzeug, und das steht seit der Trennung nur
                        // im Reiter „Bearbeiten".
                        navigate(`/t/${tripId}/karte/bearbeiten`)
                      }
                    : undefined
                }
              />
              {startsDay && (
                <div className="day-divider">{dayLabel(message.created_at)}</div>
              )}
            </div>
          )
        })}

        {olderCursor != null && (
          <button
            type="button"
            className="button button--outline"
            style={{ margin: '10px auto', width: 'auto' }}
            disabled={older.isPending}
            onClick={() => older.mutate(olderCursor)}
          >
            {older.isPending ? 'Wird geladen …' : 'Ältere Nachrichten'}
          </button>
        )}
      </div>

      {/* Hier stand lange der Satz „Schreiben kannst du im Reise-Chat an
          alle." -- der Verlauf war einseitig: sie konnte ihm schreiben, er ihr
          nicht. Jetzt geht auch die andere Richtung, und was hier hinausgeht,
          sieht ausschließlich diese eine Person. */}
      {/* Unmittelbar über der Eingabezeile: dort steht er neben der Handlung,
          um die es geht, und liegt in keinem Browser unter einer Leiste. Nicht
          im Composer selbst, denn wo statt des Feldes ein Grund steht
          (abgeschlossene Reise, pausierte Empfängerin), gehört er trotzdem
          hin. */}
      <Netzstreifen />

      {withUser && !wartetAufAuskunft && stumm === null && trip.data?.archived_at == null && (
        <p className="composer__hint composer__hint--privat">
          Was du hier schreibst, sieht nur {partnerName ?? 'diese Person'}.
        </p>
      )}

      {!wartetAufAuskunft && <Composer
        tripId={tripId}
        withUser={withUser}
        bezug={bezug}
        onBezugWeg={() => setBezug(null)}
        disabled={trip.data?.archived_at != null || stumm !== null}
        // Die abgeschlossene Reise hat Vorrang: sie gilt für alle Verläufe,
        // und ein Grund, der nur diesen einen betrifft, wäre daneben falsch.
        disabledHint={
          trip.data?.archived_at != null ? undefined : (
            <>
              {stumm?.text.replace('{name}', partnerName ?? 'Diese Person')}
              {stumm?.zurReise && (
                <>
                  {' '}
                  <Link to={`/t/${tripId}/info`}>Auf der Reiseseite ändern</Link>
                </>
              )}
            </>
          )
        }
        placeholder={
          withUser
            ? `Nachricht nur an ${partnerName ?? 'diese Person'}`
            : trip.data === undefined
              ? 'Nachricht schreiben'
              : trip.data.role === 'sender'
                ? 'Nachricht an alle Empfänger'
                : `Antwort an ${trip.data.owner.display_name}`
        }
      />}
    </>
  )
}

/**
 * Warum an diesen Menschen gerade nichts geht — oder null, wenn es geht.
 *
 * Die Reihenfolge ist die der Endgültigkeit: wer gar nicht mehr dabei ist,
 * bleibt es; ein stillgelegtes Konto kann die Verwaltung wieder freigeben; ein
 * Stichtag ist ein Handgriff auf der Reiseseite. Genannt wird immer der
 * Zustand, der die Nachricht **zuerst** aufhält.
 *
 * `{name}` wird von der Seite ersetzt — hier steht der Name noch nicht fest,
 * weil er aus derselben Liste kommt, die auch diesen Zustand liefert.
 */
function grundOhneZeile(
  partner: Member | undefined,
): { text: string; zurReise: boolean } | null {
  if (partner === undefined) {
    return {
      text: 'Diese Person ist nicht mehr in dieser Reise. '
        + 'Der Verlauf bleibt lesbar, schreiben geht nicht mehr.',
      zurReise: false,
    }
  }
  // **Selbst ausgestiegen — und das musste hierher.** Seit die
  // Empfängerliste Ausgestiegene mitführt (damit sie nicht spurlos fehlen),
  // *findet* diese Seite den Partner wieder: ohne diesen Fall fiele er durch
  // alle Prüfungen, und die Eingabezeile stünde offen, obwohl niemand mehr
  // dahinter ist. Aus dem Betrieb gemeldet, einen Tag nach dem Umbau.
  if (partner.left === true) {
    return {
      text: '{name} ist aus dieser Reise ausgestiegen. '
        + 'Der Verlauf bleibt lesbar, schreiben geht nicht mehr.',
      zurReise: false,
    }
  }
  if (partner.disabled) {
    return {
      text: 'Das Konto von {name} ist stillgelegt und kommt nirgends mehr herein.',
      zurReise: false,
    }
  }
  if (partner.cut_off) {
    return {
      text: '{name} ist aus dem Verteiler genommen und bekommt nichts Neues.',
      zurReise: true,
    }
  }
  return null
}
